import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  ADD_ORDERS,
  ADD_REFUND_REQUEST_TO_ORDER,
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  REMOVE_ORDER,
  SET_GUEST_SPECIAL_REQUESTS,
  TOUR_TRAVELLER_DETAILS_UPDATE_SUBMITTED,
  UPDATE_FLIGHT_TRAVELLER_DETAILS,
  UPDATE_ORDER,
  UPDATE_FLIGHT_RESERVATION_DETAILS,
} from 'actions/actionConstants'
import {
  FETCH_CANCELLATION_MODAL_SETTINGS,
  FETCH_FLIGHT_ORDER_PNRS,
  FETCH_ORDER,
  FETCH_ORDERS_FOR_CURRENT_USER,
  FETCH_ORDER_BY_SUBSCRIPTION,
  FETCH_ORDER_ITEM_FLIGHT_DETAILS,
  FETCH_ORDER_ITEM_CRUISE_DETAILS,
  FETCH_ROOM_REFUND_INFO,
  FETCH_TOUR_ORDER_TRAVELLER_DETAILS,
  REFUND_ORDER_ITEM,
  USER_LOGIN,
  USER_LOGOUT,
  FETCH_ORDERS_DATES_REQUESTS,
  CREATE_DATES_REQUEST,
  UPDATE_DATES_REQUEST,
  FETCH_ORDER_CANCELLATION_CONFIGS,
  UPDATE_INSURANCE,
} from 'actions/apiActionConstants'
import { arrayToObject } from 'lib/array/arrayUtils'
import { omitKeys } from 'lib/object/objectUtils'

function extractError(error: any) {
  if (typeof error === 'string') {
    return error
  }

  const { errors, message } = error
  if (Array.isArray(errors) && errors.length > 0 && errors[0].message) {
    return errors[0].message
  } else {
    return message
  }
}

export const initialOrdersState: App.OrdersState = {
  orders: {},
  cancellationConfigs: {},
  cancellationConfigsErrors: {},
  cancellationConfigsFetching: {},
  orderErrors: {},
  ordersFetching: {},
  tourOrdersTravellerDetails: {},
  error: undefined,
  fetching: {
    all: false,
    upcoming: false,
    upsellable: false,
  },
  ordersFetched: {
    all: false,
    upcoming: false,
    upsellable: false,
  },
  ordersBySubscriptionId: {},
  orderItemsProcessing: {},
  orderItemRefundErrors: {},
  flightDetails: {},
  cruiseDetails: {},
  cancellationSettings: undefined,
  fetchingCancellationSettings: false,
  cancellationSettingsError: undefined,
  itemRefundInfo: {},
  datesRequests: {},
  fetchingDatesRequests: {},
  insuranceUpdateErrors: {},
}

const apiCalls = apiReducerSwitch<App.OrdersState>({
  [FETCH_ROOM_REFUND_INFO]: {
    request: (state, action) => ({
      itemRefundInfo: {
        ...state.itemRefundInfo,
        [action.key]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      itemRefundInfo: {
        ...state.itemRefundInfo,
        [action.key]: {
          fetching: false,
          info: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      itemRefundInfo: {
        ...state.itemRefundInfo,
        [action.key]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_ORDER]: {
    request: (state, action) => ({
      ordersFetching: {
        ...state.ordersFetching,
        [action.orderId]: true,
      },
      orderErrors: {
        ...state.orderErrors,
        [action.orderId]: undefined,
      },
    }),
    success: (state, action) => ({
      orders: {
        ...state.orders,
        [action.orderId]: action.data,
      },
      ordersFetching: {
        ...state.ordersFetching,
        [action.orderId]: false,
      },
      cancellationConfigs: omitKeys([action.orderId], state.cancellationConfigs),
      cancellationConfigsErrors: omitKeys([action.orderId], state.cancellationConfigsErrors),
    }),
    failure: (state, action) => ({
      ordersFetching: {
        ...state.ordersFetching,
        [action.orderId]: false,
      },
      orderErrors: {
        ...state.orderErrors,
        [action.orderId]: action.error,
      },
    }),
  },
  [FETCH_ORDER_CANCELLATION_CONFIGS]: {
    request: (state, action) => ({
      cancellationConfigsFetching: {
        ...state.cancellationConfigsFetching,
        [action.orderId]: true,
      },
      cancellationConfigsErrors: {
        ...state.cancellationConfigsErrors,
        [action.orderId]: undefined,
      },
    }),
    success: (state, action) => ({
      cancellationConfigs: {
        ...state.cancellationConfigs,
        [action.orderId]: action.data,
      },
      cancellationConfigsFetching: {
        ...state.cancellationConfigsFetching,
        [action.orderId]: false,
      },
    }),
    failure: (state, action) => ({
      cancellationConfigsFetching: {
        ...state.cancellationConfigsFetching,
        [action.orderId]: false,
      },
      cancellationConfigsErrors: {
        ...state.cancellationConfigsErrors,
        [action.orderId]: action.error,
      },
    }),
  },
  [FETCH_ORDER_BY_SUBSCRIPTION]: {
    request: (state, action) => ({
      ordersFetching: {
        ...state.ordersFetching,
        [action.subscriptionId]: true,
      },
    }),
    success: (state, action) => ({
      orders: {
        ...state.orders,
        ...(action.data ? { [action.data.id]: action.data } : undefined),
      },
      ordersBySubscriptionId: {
        ...state.ordersBySubscriptionId,
        ...(action.data ? { [action.subscriptionId]: action.data.id } : undefined),
      },
      ordersFetching: {
        ...state.ordersFetching,
        [action.subscriptionId]: false,
      },
    }),
    failure: (state, action) => ({
      ordersFetching: {
        ...state.ordersFetching,
        [action.subscriptionId]: false,
      },
    }),
  },
  [FETCH_ORDERS_FOR_CURRENT_USER]: {
    request: (state, action) => ({
      error: undefined,
      fetching: {
        ...state.fetching,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      fetching: {
        ...state.fetching,
        [action.key]: false,
      },
      ordersFetched: {
        ...state.ordersFetched,
        [action.key]: true,
      },
      orders: {
        ...state.orders,
        ...arrayToObject<App.Order>(action.data, order => order.id),
      },
    }),
    failure: (state, action) => ({
      error: action.error,
      fetching: {
        ...state.fetching,
        [action.key]: false,
      },
    }),
  },
  [FETCH_TOUR_ORDER_TRAVELLER_DETAILS]: {
    request: (state, action) => ({
      tourOrdersTravellerDetails: {
        ...state.tourOrdersTravellerDetails,
        [action.tourOrderId]: { status: 'pending' },
      },
    }),
    success: (state, action) => ({
      tourOrdersTravellerDetails: {
        ...state.tourOrdersTravellerDetails,
        [action.tourOrderId]: {
          status: 'fetched',
          loadedAtTimeStamp: new Date().getTime(),
          travellerDetails: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      tourOrdersTravellerDetails: {
        ...state.tourOrdersTravellerDetails,
        [action.tourOrderId]: { status: 'failed', error: action.error },
      },
    }),
  },
  [FETCH_ORDER_ITEM_FLIGHT_DETAILS]: {
    request: (state, action) => ({
      flightDetails: {
        ...state.flightDetails,
        [action.key]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      flightDetails: {
        ...state.flightDetails,
        [action.key]: {
          flight: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      flightDetails: {
        ...state.flightDetails,
        [action.key]: {
          flight: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_ORDER_ITEM_CRUISE_DETAILS]: {
    request: (state, action) => ({
      cruiseDetails: {
        ...state.cruiseDetails,
        [action.key]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      cruiseDetails: {
        ...state.cruiseDetails,
        [action.key]: {
          cruise: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      cruiseDetails: {
        ...state.cruiseDetails,
        [action.key]: {
          cruise: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [REFUND_ORDER_ITEM]: {
    request: (state, action) => ({
      orderItemsProcessing: {
        ...state.orderItemsProcessing,
        [action.itemId]: true,
      },
      orderItemRefundErrors: {
        ...state.orderItemRefundErrors,
        [action.itemId]: undefined,
      },
    }),
    success: (state, action) => ({
      orders: {
        ...state.orders,
        [action.orderId]: action.data,
      },
      orderItemsProcessing: {
        ...state.orderItemsProcessing,
        [action.itemId]: false,
      },
    }),
    failure: (state, action) => ({
      orderItemsProcessing: {
        ...state.orderItemsProcessing,
        [action.itemId]: false,
      },
      orderItemRefundErrors: {
        ...state.orderItemRefundErrors,
        [action.itemId]: action.error,
      },
    }),
  },
  [FETCH_CANCELLATION_MODAL_SETTINGS]: {
    request: () => {
      return {
        fetchingCancellationSettings: true,
        cancellationSettingsError: undefined,
      }
    },
    success: (state, action) => {
      return {
        fetchingCancellationSettings: false,
        cancellationSettings: action.data,
      }
    },
    failure: (state, action) => {
      return {
        fetchingCancellationSettings: false,
        cancellationSettingsError: action.error,
      }
    },
  },
  [FETCH_ORDERS_DATES_REQUESTS]: {
    request: (state, action) => ({
      fetchingDatesRequests: {
        ...state.fetchingDatesRequests,
        [action.orderId]: true,
      },
    }),
    success: (state, action) => ({
      fetchingDatesRequests: {
        ...state.fetchingDatesRequests,
        [action.orderId]: false,
      },
      datesRequests: {
        ...state.datesRequests,
        [action.orderId]: action.data,
      },
    }),
    failure: (state, action) => ({
      fetchingDatesRequests: {
        ...state.fetchingDatesRequests,
        [action.orderId]: false,
      },
    }),
  },
  [UPDATE_DATES_REQUEST]: {
    request: (state, action) => ({
      fetchingDatesRequests: {
        ...state.fetchingDatesRequests,
        [action.datesRequest.orderId]: true,
      },
    }),
    success: (state, action) => {
      return {
        fetchingDatesRequests: {
          ...state.fetchingDatesRequests,
          [action.datesRequest.orderId]: false,
        },
        datesRequests: {
          ...state.datesRequests,
          [action.datesRequest.orderId]: state.datesRequests[action.datesRequest?.orderId]?.map((datesRequest) => datesRequest.id === action.datesRequest.id ? action.data : datesRequest) || [],
        },
      }
    },
    failure: (state, action) => ({
      fetchingDatesRequests: {
        ...state.fetchingDatesRequests,
        [action.datesRequest.orderId]: false,
      },
    }),
  },
  [UPDATE_INSURANCE]: {
    request: (state, action) => ({
      insuranceUpdateErrors: omitKeys([action.orderId], state.insuranceUpdateErrors),
    }),
    success: (state, action) => ({
      insuranceUpdateErrors: omitKeys([action.orderId], state.insuranceUpdateErrors),
    }),
    failure: (state, action) => ({
      insuranceUpdateErrors: {
        ...state.insuranceUpdateErrors,
        [action.orderId]: extractError(action.error),
      },
    }),
  },
  [USER_LOGIN]: {
    request: () => initialOrdersState,
  },
  [USER_LOGOUT]: {
    success: () => initialOrdersState,
  },
  [FETCH_FLIGHT_ORDER_PNRS]: {
    success: (state, action) => ({
      orders: {
        ...state.orders,
        [action.data.id]: {
          ...state.orders[action.data.id],
          flightItems: action.data.flightItems,
        },
      },
    }),
  },
  [SET_GUEST_SPECIAL_REQUESTS]: {
    success: (state, action) => {
      return {
        orders: {
          ...state.orders,
          [action.data.orderId]: {
            ...state.orders[action.data.orderId],
            items: state.orders[action.data.orderId].items.map(item => {
              if (item.id === action.data.itemId) {
                return {
                  ...item,
                  reservation: {
                    ...item.reservation!,
                    guestSpecialRequests: action.data.result.guest_special_requests,
                  },
                }
              }
              return item
            }),
          },
        },
      }
    },
  },
  [CREATE_DATES_REQUEST]: {
    success: (state, action) => ({
      datesRequests: {
        ...state.datesRequests,
        [action.body.orderId]: state.datesRequests[action.body.orderId] || [],
      },
    }),
  },
  [UPDATE_FLIGHT_RESERVATION_DETAILS]: {
    success: (state, action) => {
      // Preserve travellers from state — UPDATE_FLIGHT_TRAVELLER_DETAILS handles that field, and these two calls fire concurrently from FlightTravellerEditModal
      const currentFlight = state.flightDetails[action.id]?.flight
      return {
        flightDetails: {
          ...state.flightDetails,
          [action.id]: {
            ...state.flightDetails[action.id],
            flight: {
              ...currentFlight,
              ...action.data,
              travellers: currentFlight?.travellers ?? action.data.travellers,
              emergencyContact: {
                ...currentFlight?.emergencyContact,
                firstName: action.data?.emergency_first_name ?? '',
                lastName: action.data?.emergency_last_name ?? '',
                phone: action.data?.emergency_phone_number ?? '',
                relationship: action.data?.emergency_relationship ?? '',
              },
            },
          },
        },
      }
    },
  },
})

export default createAppReducer<App.OrdersState>(initialOrdersState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [UPDATE_ORDER]: (state, action) => ({
    orders: {
      ...state.orders,
      [action.order.id]: action.order,
    },
  }),
  [ADD_ORDERS]: (state, action) => ({
    orders: {
      ...state.orders,
      ...arrayToObject<App.Order>(action.orders, order => order.id),
    },
  }),
  [REMOVE_ORDER]: (state, action) => ({
    orders: omitKeys([action.orderId], state.orders),
  }),
  [TOUR_TRAVELLER_DETAILS_UPDATE_SUBMITTED]: (state, action) => {
    return ({
      tourOrdersTravellerDetails: {
        ...state.tourOrdersTravellerDetails,
        [action.tourOrderId]: {
          ...state.tourOrdersTravellerDetails[action.tourOrderId],
          travellerDetails: action.travellerDetails,
        },
      },
    })
  },
  [ADD_REFUND_REQUEST_TO_ORDER]: (state, action) => {
    const order = state.orders[action.orderId]

    if (!order) {
      return state
    }

    return {
      orders: {
        ...state.orders,
        [action.orderId]: {
          ...order,
          refundRequests: {
            ...order.refundRequests,
            [action.orderItemId]: action.refundRequest,
          },
        },
      },
    }
  },
  [UPDATE_FLIGHT_TRAVELLER_DETAILS]: (state, action) => {
    const flight = state.flightDetails[action.id]?.flight
    if (!flight) {
      return state
    }

    const updatedById = new Map(action.data.map((t: App.FlightTraveller) => [t.id, t]))

    return {
      flightDetails: {
        ...state.flightDetails,
        [action.id]: {
          ...state.flightDetails[action.id],
          flight: {
            ...flight,
            travellers: flight.travellers.map((t: App.FlightTraveller) => {
              const updated = updatedById.get(t.id)
              if (!updated) return t
              return {
                ...updated,
                dietary_requirements: updated.dietary_requirements !== undefined
                  ? updated.dietary_requirements
                  : t.dietary_requirements,
              }
            }),
          },
        },
      },
    }
  },
})
